import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import WidgetSettings from "../../components/WidgetSettings";
import { tenantAPI } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from '../../utils/logger';

const SettingsPage = () => {
  useOutletContext(); // Required for AdminLayout context
  const { tenant } = useAuth();
  const [config, setConfig] = useState({
    primary_color: "var(--g6-primary)",
    secondary_color: "var(--g6-secondary)",
    greeting_message: "Hi! How can I help you today?",
    position: "bottom-right",
    widget_size: "medium",
    show_agent_photos: true,
    show_typing_indicator: true,
    enable_sound: true,
    theme: "light",
    border_radius: "rounded",
    button_text: "Chat with us",
    offline_message: "We're currently offline. Leave us a message!",
    language: "en",
    color_style: "solid",
    logo_url: "/Logo.png",
    display_name: "",
    information_prompt: "What would you like to know?",
    sales_prompt: "What product or service are you interested in?",
    agent_prompt: "I'll connect you with an agent. Please provide your name first.",
    // Scraping settings
    max_pages_to_scrape: 200,
    website_url: "",
    // Company info
    company_name: "",
    contact_email: "",
    contact_phone: "",
  });
  const [keywords, setKeywords] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (tenant?.id) {
      fetchConfig();
    }
  }, [tenant?.id]);

  const fetchConfig = async () => {
    if (!tenant?.id) return;

    setIsLoading(true);
    try {
      // Fetch tenant-specific settings
      const settings = await tenantAPI.getTenantSettings(tenant.id);

      // Merge with defaults
      setConfig(prev => ({
        ...prev,
        ...settings
      }));

      if (settings.keywords && Array.isArray(settings.keywords)) {
        setKeywords(settings.keywords.join(", "));
      }
    } catch (error) {
      logger.error("Error fetching tenant settings:", error);
      // If tenant settings fail, the defaults will be used
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!tenant?.id) {
      alert("No tenant context available");
      return;
    }

    setIsSaving(true);
    try {
      const keywordArray = keywords.split(",").map(k => k.trim()).filter(Boolean);

      // Save to tenant-specific settings
      await tenantAPI.updateTenantSettings(tenant.id, {
        ...config,
        keywords: keywordArray
      });

      alert("Configuration saved successfully!");
    } catch (error) {
      logger.error("Error saving config:", error);
      alert("Failed to save configuration");
    } finally {
      setIsSaving(false);
    }
  };

  const embedCode = `<!-- G6th Sense Chat Widget -->
<script>
  (function() {
    window.G6thSenseConfig = {
      tenantId: '${tenant?.id || 'YOUR_TENANT_ID'}',
      widgetPublicKey: 'YOUR_WIDGET_PUBLIC_KEY'
    };
    var script = document.createElement('script');
    script.src = '${window.location.origin}/widget-embed.js';
    script.async = true;
    document.head.appendChild(script);
  })();
</script>`;

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <WidgetSettings
        config={config}
        setConfig={setConfig}
        keywords={keywords}
        setKeywords={setKeywords}
        handleSaveConfig={handleSaveConfig}
        isSaving={isSaving}
        embedCode={embedCode}
      />
    </div>
  );
};

export default SettingsPage;
